import React, { useState } from 'react';
import { MessageSquare, ThumbsDown, ThumbsUp } from 'lucide-react';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import { post } from '@/utils/apiClient';
import { toast } from 'react-toastify';
import { logger } from "../../../utils/logger";

const MessageFeedback = ({ messageId }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedFeedback, setSubmittedFeedback] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleSubmit = async () => {
    if (feedbackType === 'Message' && !message.trim()) {
      toast.error('Please enter your feedback message');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await post('/api/feedback', {
        feedback_type: feedbackType,
        message: message || `${feedbackType} feedback for message ID: ${messageId}`
      });
      
      setMessage('');
      setIsDialogOpen(false);
      setSubmittedFeedback(prev => [...prev, feedbackType]);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      logger.error('Error submitting feedback:', error);
      toast.error('An error occurred while submitting feedback.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openFeedbackDialog = (type) => {
    setFeedbackType(type);
    setIsMenuOpen(false);
    setIsDialogOpen(true);
  };

  return (
    <>
      <div className="relative flex items-center justify-end mt-0.5 transition-opacity">
        {/* Thumbs Up Button */}
        <button
          onClick={() => openFeedbackDialog('Positive')}
          className={`${
            submittedFeedback.includes('Positive') 
              ? 'text-gray-400 cursor-default' 
              : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
          } hidden p-1.5 rounded-full`}
          aria-label="Thumbs Up"
          disabled={submittedFeedback.includes('Positive')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8.864.046C7.908-.193 7.02.53 6.956 1.466c-.072 1.051-.23 2.016-.428 2.59-.125.36-.479 1.013-1.04 1.639-.557.623-1.282 1.178-2.131 1.41C2.685 7.288 2 7.87 2 8.72v4.001c0 .845.682 1.464 1.448 1.545 1.07.114 1.564.415 2.068.723l.048.03c.272.165.578.348.97.484.397.136.861.217 1.466.217h3.5c.937 0 1.599-.477 1.934-1.064a1.86 1.86 0 0 0 .254-.912c0-.152-.023-.312-.077-.464.201-.263.38-.578.488-.901.11-.33.172-.762.004-1.149.069-.13.12-.269.159-.403.077-.27.113-.568.113-.857 0-.288-.036-.585-.113-.856a2.144 2.144 0 0 0-.138-.362 1.9 1.9 0 0 0 .234-1.734c-.206-.592-.682-1.1-1.2-1.272-.847-.282-1.803-.276-2.516-.211a9.84 9.84 0 0 0-.443.05 9.365 9.365 0 0 0-.062-4.509A1.38 1.38 0 0 0 9.125.111L8.864.046zM11.5 14.721H8c-.51 0-.863-.069-1.14-.164-.281-.097-.506-.228-.776-.393l-.04-.024c-.555-.339-1.198-.731-2.49-.868-.333-.036-.554-.29-.554-.55V8.72c0-.254.226-.543.62-.65 1.095-.3 1.977-.996 2.614-1.708.635-.71 1.064-1.475 1.238-1.978.243-.7.407-1.768.482-2.85.025-.362.36-.594.667-.518l.262.066c.16.04.258.143.288.255a8.34 8.34 0 0 1-.145 4.725.5.5 0 0 0 .595.644l.003-.001.014-.003.058-.014a8.908 8.908 0 0 1 1.036-.157c.663-.06 1.457-.054 2.11.164.175.058.45.3.57.65.107.308.087.67-.266 1.022l-.353.353.353.354c.043.043.105.141.154.315.048.167.075.37.075.581 0 .212-.027.414-.075.582-.05.174-.111.272-.154.315l-.353.353.353.354c.047.047.109.177.005.488a2.224 2.224 0 0 1-.505.805l-.353.353.353.354c.006.005.041.05.041.17a.866.866 0 0 1-.121.416c-.165.288-.503.56-1.066.56z"/>
          </svg>
        </button>
        
        {/* Thumbs Down Button */}
        <button
          onClick={() => openFeedbackDialog('Negative')}
          className={`${
            submittedFeedback.includes('Negative') 
              ? 'text-gray-400 cursor-default' 
              : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
          } hidden p-1.5 rounded-full`}
          aria-label="Thumbs Down"
          disabled={submittedFeedback.includes('Negative')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M8.864 15.674c-.956.24-1.843-.484-1.908-1.42-.072-1.05-.23-2.015-.428-2.59-.125-.36-.479-1.012-1.04-1.638-.557-.624-1.282-1.179-2.131-1.41C2.685 8.432 2 7.85 2 7V3c0-.845.682-1.464 1.448-1.546 1.07-.113 1.564-.415 2.068-.723l.048-.029c.272-.166.578-.349.97-.484C6.931.08 7.395 0 8 0h3.5c.937 0 1.599.478 1.934 1.064.164.287.254.607.254.913 0 .152-.023.312-.077.464.201.262.38.577.488.9.11.33.172.762.004 1.15.069.13.12.268.159.403.077.27.113.567.113.856 0 .289-.036.586-.113.856-.035.12-.08.244-.138.363.394.571.418 1.2.234 1.733-.206.592-.682 1.1-1.2 1.272-.847.283-1.803.276-2.516.211a9.877 9.877 0 0 1-.443-.05 9.364 9.364 0 0 1-.062 4.51c-.138.508-.55.848-1.012.964l-.261.065zM11.5 1H8c-.51 0-.863.068-1.14.163-.281.097-.506.229-.776.393l-.04.025c-.555.338-1.198.73-2.49.866-.333.035-.554.29-.554.55v4c0 .255.226.543.62.65 1.095.3 1.977.997 2.614 1.709.635.71 1.064 1.475 1.238 1.977.243.7.407 1.768.482 2.85.025.362.36.595.667.518l.262-.065c.16-.04.258-.144.288-.255a8.34 8.34 0 0 0-.145-4.726.5.5 0 0 1 .595-.643h.003l.014.004.058.013a8.912 8.912 0 0 0 1.036.157c.663.06 1.457.054 2.11-.163.175-.059.45-.301.57-.651.107-.308.087-.67-.266-1.021l-.353-.354.353-.354c.043-.042.105-.14.154-.315.048-.167.075-.37.075-.581 0-.211-.027-.414-.075-.581-.05-.174-.111-.273-.154-.315l-.353-.354.353-.354c.047-.047.109-.176.005-.488a2.224 2.224 0 0 0-.505-.804l-.353-.354.353-.354c.006-.005.041-.05.041-.17a.866.866 0 0 0-.121-.415C12.4 1.272 12.063 1 11.5 1z"/>
          </svg>
        </button>
        
        {/* Message Feedback Button */}
        <button
          onClick={() => setIsMenuOpen(prev => !prev)}
          className={`${
            submittedFeedback.includes('Message') 
              ? 'text-gray-400 cursor-default' 
              : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
          } p-1.5 rounded-full`}
          aria-label="Message feedback"
          aria-expanded={isMenuOpen}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z"/>
            <path d="M3 3.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zM3 6a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9A.5.5 0 0 1 3 6zm0 2.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5z"/>
          </svg>
        </button>
        {isMenuOpen && (
          <div className="absolute right-0 top-full z-30 mt-1 min-w-36 rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-600 dark:bg-gray-800">
            <button
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => openFeedbackDialog('Positive')}
              disabled={submittedFeedback.includes('Positive')}
            >
              <ThumbsUp size={13} /> Helpful
            </button>
            <button
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => openFeedbackDialog('Negative')}
              disabled={submittedFeedback.includes('Negative')}
            >
              <ThumbsDown size={13} /> Not helpful
            </button>
            <button
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => openFeedbackDialog('Message')}
              disabled={submittedFeedback.includes('Message')}
            >
              <MessageSquare size={13} /> Comment
            </button>
          </div>
        )}
      </div>
      
      {/* Feedback Dialog */}
      <ConfirmationDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleSubmit}
        title={`${feedbackType === 'Message' ? 'Submit Feedback' : feedbackType === 'Positive' ? 'Thumbs Up Feedback' : 'Thumbs Down Feedback'}`}
        message={
          feedbackType === 'Message' 
            ? "Please share your thoughts, suggestions, or report issues..."
            : feedbackType === 'Positive'
              ? "Thanks for the positive feedback! Would you like to add any additional comments?"
              : "We're sorry this response wasn't helpful. Would you like to tell us how we can improve?"
        }
        confirmText="Submit"
        cancelText="Cancel"
        confirmButtonClass="bg-blue-500 hover:bg-blue-600"
        extraContent={
          feedbackType === 'Message' ? (
            <textarea
              className="w-full p-2 mt-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Your feedback"
              rows={4}
              required
            />
          ) : (
            <textarea
              className="w-full p-2 mt-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Optional comments"
              rows={4}
            />
          )
        }
      />
    </>
  );
};

export default MessageFeedback;
